import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  ElementRef,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { LucideModule } from '../../../shared/modules/lucide.module';
import {
  LinkDinamico,
  LocalLinkDinamico,
  iconeDaAbertura,
  rotuloDaAbertura,
  VarianteLinkDinamico,
  varianteDoLocal,
} from './links-dinamicos.model';
import { LinksDinamicosService } from './links-dinamicos.service';

/**
 * Publica no portal os links configurados na área administrativa. O local recebido escolhe o
 * layout — atalho na home, card de serviço nas listagens e card com imagem nas notícias — para
 * que cada tela mantenha a aparência que já tinha.
 */
@Component({
  selector: 'app-links-dinamicos',
  templateUrl: './links-dinamicos.component.html',
  styleUrls: ['./links-dinamicos.component.css'],
  imports: [LucideModule, NgTemplateOutlet],
})
export class LinksDinamicosComponent {
  private readonly servico = inject(LinksDinamicosService);
  private readonly router = inject(Router);
  private readonly sanitizer = inject(DomSanitizer);

  readonly local = input.required<LocalLinkDinamico>();

  /**
   * Layout do card. Em branco ele vem do local; a tela informa quando o mesmo local aparece de
   * formas diferentes — caso de 'Todos os Serviços', card em categorias e item em todos-serviços.
   */
  readonly variante = input<VarianteLinkDinamico | ''>('');

  /**
   * Telas paginadas passam a comparação da página atual com a primeira: como os links ficam fora
   * do recorte da paginação, sem isso eles se repetiriam ao fim de todas as páginas.
   */
  readonly exibir = input(true);

  readonly links = computed(() =>
    this.exibir() ? this.servico.porLocal(this.local()) : []
  );

  readonly varianteEfetiva = computed(() => this.variante() || varianteDoLocal(this.local()));

  private readonly modal = viewChild<ElementRef<HTMLDialogElement>>('modal');

  readonly linkNoModal = signal<LinkDinamico | null>(null);

  readonly urlDoModal = computed<SafeResourceUrl | null>(() => {
    const link = this.linkNoModal();

    return link ? this.sanitizer.bypassSecurityTrustResourceUrl(link.urlDestino) : null;
  });

  readonly iconeDaAbertura = iconeDaAbertura;
  readonly rotuloDaAbertura = rotuloDaAbertura;

  /** Descrição para leitores de tela: o nome mais o que vai acontecer ao clicar. */
  descricaoAcessivel(link: LinkDinamico): string {
    return `${link.nome} — ${rotuloDaAbertura(link.tipoAbertura)}`;
  }

  /**
   * O download é o único comportamento que continua sendo uma âncora de verdade: o atributo
   * `download` só funciona no elemento, não dá para reproduzir por script.
   */
  ehDownload(link: LinkDinamico): boolean {
    return link.tipoAbertura === 'Download arquivo';
  }

  nomeDoArquivo(link: LinkDinamico): string {
    const caminho = link.urlDestino.split('?')[0].split('#')[0];

    return caminho.split('/').pop() || link.nome;
  }

  abrir(link: LinkDinamico, evento: Event): void {
    evento.preventDefault();

    switch (link.tipoAbertura) {
      case 'Nova aba':
        window.open(link.urlDestino, '_blank', 'noopener');
        return;

      case 'Modal interno':
        this.abrirModal(link);
        return;

      case 'Download arquivo':
        // Tratado pela âncora; aqui só cobre o caso de o clique chegar por outro caminho.
        window.open(link.urlDestino, '_blank', 'noopener');
        return;

      default:
        this.navegar(link.urlDestino);
    }
  }

  fecharModal(): void {
    this.modal()?.nativeElement.close();
    this.linkNoModal.set(null);
  }

  /** Sai do modal para o destino em nova aba, quando o conteúdo não puder ser embutido. */
  abrirEmNovaAba(): void {
    const link = this.linkNoModal();

    if (link) {
      window.open(link.urlDestino, '_blank', 'noopener');
    }
  }

  private abrirModal(link: LinkDinamico): void {
    this.linkNoModal.set(link);
    this.modal()?.nativeElement.showModal();
  }

  /** Rota do próprio portal navega pelo router; endereço externo troca a página. */
  private navegar(url: string): void {
    if (/^https?:\/\//i.test(url)) {
      window.location.href = url;
      return;
    }

    this.router.navigateByUrl(url);
  }
}
